import { AxiosError } from 'axios';
import { apiService } from '@/shared/services/apiServices';
import { SERVICE_TYPE_ERROR_MESSAGES } from '../errors';
import { ServiceTypeStatus } from '../types/serviceTypeTypes';
import serviceTypeEndpoints from './serviceTypeEndpoints';

export interface UpdateServiceTypeStatusParams {
  serviceTypeId: string;
  status: ServiceTypeStatus;
}

export interface UpdateServiceTypeStatusResult {
  success: boolean;
  status?: ServiceTypeStatus;
  error?: string;
}

const parseStatus = (data: unknown): ServiceTypeStatus | undefined => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const value = (data as { status?: unknown }).status;
  if (
    value === ServiceTypeStatus.Active ||
    value === ServiceTypeStatus.Inactive
  ) {
    return value;
  }
  return undefined;
};

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return SERVICE_TYPE_ERROR_MESSAGES.DEFAULT_ERROR;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    return (
      apiError.message ||
      apiError.error ||
      SERVICE_TYPE_ERROR_MESSAGES.DEFAULT_ERROR
    );
  }
  return SERVICE_TYPE_ERROR_MESSAGES.DEFAULT_ERROR;
};

export const updateServiceTypeStatusApiService = {
  updateServiceTypeStatus: async ({
    serviceTypeId,
    status,
  }: UpdateServiceTypeStatusParams): Promise<UpdateServiceTypeStatusResult> => {
    const trimmedId = serviceTypeId?.trim();
    if (!trimmedId) {
      return {
        success: false,
        error: SERVICE_TYPE_ERROR_MESSAGES.DEFAULT_ERROR,
      };
    }

    try {
      const result = await apiService.patchAPI(
        `${serviceTypeEndpoints.SERVICE_TYPES}/${trimmedId}/status`,
        { status }
      );

      if (!result.error || result.status === 204) {
        const parsed = parseStatus(result.data);
        return parsed ? { success: true, status: parsed } : { success: true };
      }

      if (result.isNetworkError) {
        return {
          success: false,
          error: SERVICE_TYPE_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      return {
        success: false,
        error: extractErrorMessage(result.data),
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        const isNetworkError = !error.response && !!error.request;
        if (isNetworkError) {
          return {
            success: false,
            error: SERVICE_TYPE_ERROR_MESSAGES.NETWORK_ERROR,
          };
        }
        return {
          success: false,
          error: extractErrorMessage(error.response?.data),
        };
      }
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : SERVICE_TYPE_ERROR_MESSAGES.UNEXPECTED_ERROR,
      };
    }
  },
};
